import React, { useEffect, useState } from 'react';
import carnetService from '../services/carnetService';
import solicitudIngresoService from '../services/solicitudIngresoService';
import '../styles/EditarFormatos.css';

const EditarFormatos = () => {
  const [loading, setLoading] = useState(true);
  const [, setCarnet] = useState(null);
  const [, setSolicitud] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState('carnet'); // Acordeón

  // Form state for carnet
  const [nombreDirectora, setNombreDirectora] = useState('');
  const [cedulaDirectora, setCedulaDirectora] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Form state for solicitud
  const [parrafo, setParrafo] = useState('');
  const [nombreDirectoraSolicitud, setNombreDirectoraSolicitud] = useState('');
  const [logoFileSolicitud, setLogoFileSolicitud] = useState(null);
  const [logoPreviewSolicitud, setLogoPreviewSolicitud] = useState(null);

  // Errores
  const [errors, setErrors] = useState({});

  const handleAccordion = (section) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const cData = await carnetService.ensureFormatExists().then(async (r)=>{
          if (r.ok && r.id) {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/formatocarnet/${encodeURIComponent(r.id)}`);
            if (res.ok) return res.json();
          }
          return null;
        }).catch(()=>null);
        if (cData) {
          setCarnet(cData);
          setNombreDirectora(cData?.FORMATOCARNET_NOMBREDIRECTORA || '');
          setCedulaDirectora(cData?.FORMATOCARNET_CEDULADIRECTORA || '');
        }
      } catch (e) {
        // ignore, services can handle
      }

      try {
        const sData = await solicitudIngresoService.getCurrentFormat().catch(()=>null);
        if (sData) {
          setSolicitud(sData);
          setParrafo(sData?.SOLICITUDINGRESO_PARRAFODESCRIPTIVO || '');
          setNombreDirectoraSolicitud(sData?.SOLICITUDINGRESO_NOMBREDIRECTORA || '');
        }
      } catch (e) {
        // ignore
      }

      setLoading(false);
    };
    load();
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoSolicitudChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFileSolicitud(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreviewSolicitud(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCarnet = async (e) => {
    e.preventDefault();
    setErrors({});
    
    // Validaciones
    const newErrors = {};
    if (!nombreDirectora.trim()) newErrors.nombreDirectora = 'El nombre de la directora es obligatorio';
    if (!cedulaDirectora.trim()) newErrors.cedulaDirectora = 'La cédula de la directora es obligatoria';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const res = await carnetService.createFormat({ nombreDirectora, cedulaDirectora, logoFile });
      if (res.success) {
        alert('Formato de carnet guardado exitosamente');
        setCarnet(res.data);
        setLogoFile(null);
      } else {
        alert(res.error || 'No se pudo guardar formato de carnet');
      }
    } catch (err) {
      console.error(err);
      alert('Error guardando formato de carnet');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSolicitud = async (e) => {
    e.preventDefault();
    setErrors({});
    
    // Validaciones
    const newErrors = {};
    if (!parrafo.trim()) newErrors.parrafo = 'El párrafo descriptivo es obligatorio';
    if (!nombreDirectoraSolicitud.trim()) newErrors.nombreDirectoraSolicitud = 'El nombre de la directora es obligatorio';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      // Using solicitudIngresoService which supports create/update with multipart
      const form = new FormData();
      form.append('parrafo', parrafo || '');
      form.append('nombreDirectora', nombreDirectoraSolicitud || '');
      if (logoFileSolicitud) form.append('logo', logoFileSolicitud);

      // Try to POST and on conflict fall back to PUT inside service
      const res = await solicitudIngresoService.createOrUpdateFromForm(form);
      if (res?.success) {
        alert('Formato de solicitud guardado exitosamente');
        setSolicitud(res.data);
        setLogoFileSolicitud(null);
      } else {
        alert(res?.error || 'No se pudo guardar formato de solicitud');
      }
    } catch (err) {
      console.error(err);
      alert('Error guardando formato de solicitud');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Cargando formatos…</div>;

  return (
    <div className="editar-formatos-container">
      <div className="editar-formatos-header">
        <h2 className="editar-formatos-title">Administrar Formatos</h2>
      </div>

      <div className="editar-formatos-form">
        {/* Sección Formato Carnet */}
        <div className="accordion-section">
          <button
            type="button"
            className={`accordion-header ${openSection === 'carnet' ? 'active' : ''}`}
            onClick={() => handleAccordion('carnet')}
          >
            <span>Formato de Carnet</span>
            <span className="accordion-chevron">›</span>
          </button>
          {openSection === 'carnet' && (
            <div className="accordion-content">
              <form onSubmit={handleSaveCarnet}>
                <div className="form-row">
                  <div className="form-group">
                    <label>NOMBRE DE LA DIRECTORA (*)</label>
                    <input
                      type="text"
                      value={nombreDirectora}
                      onChange={e => setNombreDirectora(e.target.value)}
                      className={errors.nombreDirectora ? 'error' : ''}
                      placeholder="Ingrese el nombre completo"
                    />
                    {errors.nombreDirectora && <span className="error-message">{errors.nombreDirectora}</span>}
                  </div>
                  <div className="form-group">
                    <label>CÉDULA DE LA DIRECTORA (*)</label>
                    <input
                      type="text"
                      value={cedulaDirectora}
                      onChange={e => setCedulaDirectora(e.target.value)}
                      className={errors.cedulaDirectora ? 'error' : ''}
                      maxLength={20}
                      placeholder="Ingrese la cédula"
                    />
                    {errors.cedulaDirectora && <span className="error-message">{errors.cedulaDirectora}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>LOGO DE LA FUNDACIÓN</label>
                    <div className="logo-upload-section">
                      {logoPreview && (
                        <div className="logo-preview">
                          <img src={logoPreview} alt="Logo preview" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      <span className="file-hint">Formatos permitidos: PNG, JPG, JPEG</span>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-button" disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar Formato Carnet'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Sección Formato Solicitud de Ingreso */}
        <div className="accordion-section">
          <button
            type="button"
            className={`accordion-header ${openSection === 'solicitud' ? 'active' : ''}`}
            onClick={() => handleAccordion('solicitud')}
          >
            <span>Formato de Solicitud de Ingreso</span>
            <span className="accordion-chevron">›</span>
          </button>
          {openSection === 'solicitud' && (
            <div className="accordion-content">
              <form onSubmit={handleSaveSolicitud}>
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>PÁRRAFO DESCRIPTIVO (*)</label>
                    <textarea
                      value={parrafo}
                      onChange={e => setParrafo(e.target.value)}
                      className={errors.parrafo ? 'error' : ''}
                      rows={6}
                      placeholder="Ingrese el texto descriptivo que aparecerá en la solicitud"
                    />
                    {errors.parrafo && <span className="error-message">{errors.parrafo}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>NOMBRE DE LA DIRECTORA (*)</label>
                    <input
                      type="text"
                      value={nombreDirectoraSolicitud}
                      onChange={e => setNombreDirectoraSolicitud(e.target.value)}
                      className={errors.nombreDirectoraSolicitud ? 'error' : ''}
                      placeholder="Ingrese el nombre completo"
                    />
                    {errors.nombreDirectoraSolicitud && <span className="error-message">{errors.nombreDirectoraSolicitud}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>LOGO DE LA FUNDACIÓN</label>
                    <div className="logo-upload-section">
                      {logoPreviewSolicitud && (
                        <div className="logo-preview">
                          <img src={logoPreviewSolicitud} alt="Logo preview" />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoSolicitudChange}
                      />
                      <span className="file-hint">Formatos permitidos: PNG, JPG, JPEG</span>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-button" disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar Formato Solicitud'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditarFormatos;
